"use client";

import {
  AppSearchInteractionOption,
  PageSizeProvider,
  Pagination,
  useEmitAppSearchInteractionEvent,
  usePageSize,
} from "@zapier/shared";
import { useTranslations } from "@zapier/i18n";
import { AppsModal } from "./types";

import { useAppsModalStates } from "./useAppsModalStates";

import { AppsHeader } from "./AppsHeader";
import AppsTable from "./AppsTable/AppsTable";
import { useAppsList } from "./useAppsList";

function AppsWithPageSize() {
  const t = useTranslations("apps-table");
  const { size: pageSize } = usePageSize();
  const { emit } = useEmitAppSearchInteractionEvent();

  const { setCurrentAppModal, currentAppModal } = useAppsModalStates();

  const handleSetAddConnectionModalOpen = () => {
    setCurrentAppModal(AppsModal.AddConnection);
    emit(AppSearchInteractionOption.AddConnection);
  };

  const {
    apps,
    isFetching,
    isLoading,
    isSortEnabled,
    isSortDisabledDueToLimit,
    onSortChange,
    paginationPage,
    paginationTotalCount,
    paginationTotalPages,
    sortDescriptor,
  } = useAppsList({
    pageSize,
    isRedesignEnabled: true,
  });

  // Show tooltip explaining why sorting is disabled when over app limit
  const sortDisabledTooltip = isSortDisabledDueToLimit
    ? t("sort.disabled-tooltip")
    : undefined;

  return (
    <>
      <AppsHeader onAddConnection={handleSetAddConnectionModalOpen} />
      <AppsTable
        allowsSorting={isSortEnabled}
        apps={apps}
        ariaLabel={t("aria-labels.table")}
        currentAppModal={currentAppModal}
        isLoading={isLoading || isFetching}
        onAddConnection={handleSetAddConnectionModalOpen}
        onSortChange={onSortChange}
        setCurrentAppModal={setCurrentAppModal}
        sortDescriptor={sortDescriptor}
        sortDisabledTooltip={sortDisabledTooltip}
      />
      <Pagination
        page={paginationPage}
        totalPages={paginationTotalPages}
        totalCount={paginationTotalCount}
        isLoading={isLoading || isFetching}
      />
    </>
  );
}

/**
 * Wrap this component in PageSizeProvider so that the context is available
 * to the Table and Pagination components above.
 */
export function Apps() {
  return (
    <div>
      <PageSizeProvider>
        <AppsWithPageSize />
      </PageSizeProvider>
    </div>
  );
}
